import { Component, OnInit, ViewChild } from '@angular/core'
import { filter, map, mergeMap, pluck, take, tap } from 'rxjs/operators'
import { ActivatedRoute, Router } from '@angular/router'
import { DealConfigurationQuery } from '../../../../../../generated/graphql'
import { combineLatest, Observable } from 'rxjs'
import { FormGroup } from '@angular/forms'
import { ConfigurationRoute, ConfigurationRouteService } from '../../services/configurationRoute.service'
import { ConfigurationOptionCategory } from '../../services/configurationCategory.service'
import { AuthenticationService } from '../../../../../../framework/authentication/services/authentication.service'
import { UserProfileService } from '../../../../../../framework/authentication/services/user-profile.service'
import { UserProfile } from '../../../../../../framework/authentication/models/UserProfile'
import { ConfigurationOptionsFactoryComponent } from '../../components/configuration-options-factory/configuration-options-factory.component'
import { DealQueryService } from 'modules/deals/services/dealQuery.service'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'

@UntilDestroy()
@Component({
    selector: 'mg-options',
    templateUrl: './options.component.html',
    styleUrls: ['./options.component.scss'],
})
export class OptionsComponent implements OnInit {
    form: FormGroup = new FormGroup({})
    configuration$: Observable<DealConfigurationQuery['Deal']>
    route$: Observable<ConfigurationRoute>
    // if user was not logged in on bootstrap a login submit button should be shown
    userIsLoggedInOnInit: boolean
    nextRouteAvailable$: Observable<boolean>
    modelName$: Observable<string>
    modelName: string
    leasingType$: Observable<string>
    leasingType: string
    routeCategory$: Observable<ConfigurationOptionCategory>
    userProfileValid: boolean = true
    showDownloadPDF: string
    configurationId: string

    @ViewChild(ConfigurationOptionsFactoryComponent)
    private configurationOptionsFactory: ConfigurationOptionsFactoryComponent

    private configurationId$: Observable<string>
    private needsUserProfile$: Observable<boolean>
    private previousRouteAvailable$: Observable<boolean>

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private configurationRouteService: ConfigurationRouteService,
        private authenticationService: AuthenticationService,
        private userProfileService: UserProfileService,
        private dealQueryService: DealQueryService
    ) {}

    ngOnInit() {
        this.userIsLoggedInOnInit = this.authenticationService.isLoggedIn

        this.configurationId$ = this.route.parent.data.pipe(
            untilDestroyed(this),
            pluck<any, string>('configurationId'),
            filter(Boolean),
            tap<string>(configurationId => (this.configurationId = configurationId))
            // shareReplay(1)
        )

        // make sure userProfile is loaded if user is loggedIn
        const userProfile$ = this.userProfileService.userProfile.pipe(
            untilDestroyed(this),
            filter(userProfile => !!userProfile)
        )

        // current configuration
        this.configuration$ = this.configurationId$.pipe(
            untilDestroyed(this),
            mergeMap(id => this.dealQueryService.watchDealConfiguration(id))
        )

        this.modelName$ = this.configuration$.pipe(
            untilDestroyed(this),
            pluck<DealConfigurationQuery['Deal'], string>('modelName'),
            map(modelName => (modelName || '').toLowerCase().replace(/ /g, '_'))
        )

        this.modelName$.pipe(untilDestroyed(this)).subscribe(modelName => {
            this.modelName = modelName
        })

        this.leasingType$ = this.configuration$.pipe(
            untilDestroyed(this),
            pluck<DealConfigurationQuery['Deal'], string>('leasingType'),
            map(leasingType => leasingType || '')
        )

        this.leasingType$.pipe(untilDestroyed(this)).subscribe(leasingType => {
            this.leasingType = leasingType
        })

        // curent route within the configuration
        this.route$ = this.configurationId$.pipe(
            untilDestroyed(this),
            mergeMap(configurationId => this.configurationRouteService.getActiveConfigurationRoute(configurationId))
        )

        this.routeCategory$ = this.route$.pipe(
            filter(Boolean),
            pluck<ConfigurationRoute, ConfigurationOptionCategory>('category')
        )

        this.previousRouteAvailable$ = this.route$.pipe(
            map(route => (!route || !route.previousRoute ? false : route.previousRoute.isAvailable))
        )

        this.nextRouteAvailable$ = this.route$.pipe(
            map(route => (!route || !route.nextRoute ? false : route.nextRoute.isAvailable))
        )

        this.needsUserProfile$ = combineLatest(this.previousRouteAvailable$, userProfile$).pipe(
            take(1),
            map<[boolean, UserProfile], boolean>(
                ([previousRouteAvailable, userProfile]) =>
                    previousRouteAvailable &&
                    (!userProfile.firstName || !userProfile.lastName || !userProfile.telephoneNumber)
            ),
            tap(needsUserProfile => {
                if (needsUserProfile) {
                    this.userProfileValid = false
                }
            })
        )
    }

    toNextStep() {
        if (
            this.form &&
            this.form.valid &&
            !this.form.disabled &&
            this.authenticationService.isLoggedIn &&
            this.userProfileValid
        ) {
            this.form.disable()

            this.route$.pipe(take(1)).subscribe(route => {
                this.router.navigateByUrl(route.nextRoute.path)
            })
        }
    }

    onUserProfileChange(valid: boolean) {
        this.userProfileValid = valid
        if (valid) {
            this.userProfileService.persistUserProfile()
        }
    }
}
